import {
  PermissionStatus,
  AcademicEnrollmentType,
  AcademicEnrollmentTypeCode,
  AcademicTerm,
  Coach,
} from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';
import type { WritableDraft } from 'immer';

import { USER_PROFILE_FIELDS } from './profile.constants';
import { baseQueryWithAuth } from '../baseQuery';
import { dashboardFeaturedApi } from '../dashboardFeatured/dashboardFeatured.api';
import { selectIsSharedSessionActive } from '../sharedSession/sharedSession';
import type { AppDispatch, RootState } from '../store';

export type GradeChangePreview = Readonly<{
  requiresConfirmation: boolean;
  // 'not-started' is a program the student says hasn't begun: the grade stays
  // put and it is their past results that come off the timeline.
  kind: 'grade-change' | 'not-started';
  currentGrade: string | null;
  newGrade: string | null;
}>;

export type ProfilePermissions = Readonly<{
  edit_profile: PermissionStatus;
  view_profile: PermissionStatus;
  access_team: PermissionStatus;
  crud_user: PermissionStatus;
  direct_message: PermissionStatus;
  meet: PermissionStatus;
  invest: PermissionStatus;
  back: PermissionStatus;
  propose: PermissionStatus;
  read_user: PermissionStatus;
  view_full_name: PermissionStatus;
}>;

type AutocompleteResultItem = Record<string, string>;

type AutocompleteResponse = Readonly<{
  results: AutocompleteResultItem[];
}>;

// Base properties that all API autocomplete options have
type BaseApiAutocompleteOption = Readonly<{
  label: string;
  value: string;
  name?: string;
}>;

// School-specific API autocomplete (e.g., alma maters)
export type SchoolApiAutocompleteOption = BaseApiAutocompleteOption & {
  school: string;
  institution_id?: number | null;
  id?: number;
  city?: string;
  state?: string;
  logo_media_url?: string | null;
};

// Club-specific API autocomplete
type ClubApiAutocompleteOption = BaseApiAutocompleteOption & {
  club: string;
  institution_id?: number | null;
  id: number;
};

// Language-specific API autocomplete
type LanguageApiAutocompleteOption = BaseApiAutocompleteOption & {
  language: string;
  id: number;
};

// Generic named API autocomplete option
type NamedApiAutocompleteOption = BaseApiAutocompleteOption & {
  name: string;
  id: number;
};

// Export the union of all possible API autocomplete types
export type AutocompleteOption =
  | BaseApiAutocompleteOption
  | SchoolApiAutocompleteOption
  | ClubApiAutocompleteOption
  | LanguageApiAutocompleteOption
  | NamedApiAutocompleteOption;

export type AutocompleteResult = Readonly<{
  endpoint: string;
  values: AutocompleteOption[];
  searchTerm?: string;
}>;

export type ProfileValueOption = Readonly<{
  id: number;
  name: string;
  value?: string;
}>;

export type StoryDocument = Readonly<{
  id: number;
  user_id: number;
  category_id: string;
  question_id: string;
  option_id: 'resume' | 'transcript' | 'other' | 'purpose_video' | 'personal_assessment' | null;
  profile_option_answers_id: number;
  title: string;
  description: string;
  visibility: string;
  media_id?: number;
  media_type: 'text' | 'txt' | 'document' | 'video' | 'pdf' | 'docx' | 'image' | 'link';
  media_url: string;
  uri?: string;

  // Populated only for documents added via a link, not file uploads.
  source_url?: string;
  refresh_policy?: 'live' | 'once';
  viewable?: 'true' | 'false';

  // LinkedInJobsData-specific fields
  job_title?: string;
  company_name?: string;
  dates?: string;
  created_at?: string;
  updated_at?: string;
  source?: string | null;
  school_name?: string;
  certification_received?: string;
  skill?: string;
}>;

export type LinkedInUrlData = Readonly<{
  id: number;
  user_id: number;
  value: string;
  option_id?: string | null;
  suboption_id?: string | null;
  created_at?: string;
  updated_at?: string;
  source?: string | null;
  category_id: string;
  question_id: string;
}>;

export type ProfileValueOptionResponse = Readonly<{
  academicMetrics: {
    sat_score?: ProfileValueOption;
    act_score?: ProfileValueOption;
    psat_score?: ProfileValueOption;
    high_school_gpa?: ProfileValueOption;
    post_secondary_gpa?: ProfileValueOption;
  };
  high_school_gpa?: ProfileValueOption;
  post_secondary_gpa?: ProfileValueOption;

  major_gpa?: ProfileValueOption;
  sat_score?: ProfileValueOption;
  act_score?: ProfileValueOption;
  psat_score?: ProfileValueOption;

  academicEnrollmentTypes: (ProfileValueOption & {
    level: AcademicEnrollmentType;
    code: AcademicEnrollmentTypeCode;
  })[];
  academicsHighSchoolCurrentEnrollment: ProfileValueOption[];
  academicsPostSecondaryCurrentEnrollment: ProfileValueOption[];
  academicHighSchoolExpectedGraduationYear: ProfileValueOption[];
  academicPostSecondaryExpectedGraduationYear: ProfileValueOption[];
  academicHighSchoolProgramStartTerm: ProfileValueOption[];
  academicHighSchoolProgramStartYear: ProfileValueOption[];
  academicPostSecondaryProgramStartTerm: ProfileValueOption[];
  academicPostSecondaryProgramStartYear: ProfileValueOption[];
  academicsAlmaMaters: { id: number; school: string }[];

  academicApTests: ProfileValueOption[];
  academicEntranceExams: ProfileValueOption[];

  academicExtracurricularInvolvement: ProfileValueOption[];
  academicRigor: ProfileValueOption[];
  academicDegrees: ProfileValueOption[];
  academicMajors: (ProfileValueOption & { major_gpa: number; academic_majors_id: number })[];
  academicEducationalExperiences: ProfileValueOption[];

  academicsToggleHighSchool: ProfileValueOption[];
  academicsTogglePostSecondary: ProfileValueOption[];
  academicsTogglePostGraduate: ProfileValueOption[];

  demographicsFamilyRoles: ProfileValueOption[];
  demographicsChildhoodExperiences: ProfileValueOption[];
  demographicsRelationshipExperiences: ProfileValueOption[];
  demographicsReligions: ProfileValueOption[];
  demographicsGenders: ProfileValueOption[];
  demographicsEthnicities: ProfileValueOption[];
  demographicsAffinities: ProfileValueOption[];
  demographicsMyersBriggs: ProfileValueOption[];
  demographicsHigh5s: ProfileValueOption[];
  demographicsStrengthsFinders: ProfileValueOption[];
  demographicsAstrologySigns: ProfileValueOption[];

  calendlyData: {
    calendly_url?: LinkedInUrlData[];
  };

  careerExperiences: ProfileValueOption[];
  careerOccupation: ProfileValueOption[];
  careerPaths: ProfileValueOption[];
  careerEmployer: ProfileValueOption[];

  financialProfile: ProfileValueOption[];
  financialFamilyIncomes: ProfileValueOption[];
  financialCredentials: ProfileValueOption[];
  financialExperiences: ProfileValueOption[];

  healthExperiences: ProfileValueOption[];
  healthInterests: ProfileValueOption[];
  healthMentalHealthInterests: ProfileValueOption[];
  healthFoodNutrition: ProfileValueOption[];

  hobbiesCreativeInterests: ProfileValueOption[];
  hobbiesPassions: ProfileValueOption[];
  hobbiesFavorites: ProfileValueOption[];

  resourcesClubs: { id: number; club: string }[];
  resourcesLanguages: { id: number; language: string }[];

  documentsResumes: StoryDocument[];
  documentsTranscripts: StoryDocument[];
  documentsPersonalAssessment: StoryDocument[];
  documentsOther: StoryDocument[];

  documentsStoryVideos: StoryDocument[];
  documentsStoryVideosGroup: StoryDocument[];
  documentsPurposeVideo: StoryDocument[];

  profileImage: { id: number; name: string }[];
  profileBannerImage: { id: number; name: string }[];

  linkedInData: {
    linkedIn_url?: LinkedInUrlData[];
    linkedIn_jobs?: StoryDocument[];
    linkedIn_education?: StoryDocument[];
    linkedIn_skills?: StoryDocument[];
  };

  user_bio: string;

  publicProfile: {
    id?: number;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    profile_image?: string;
    profile_banner?: string;
    city?: string;
    state?: string;
  };
}>;

export type ProfileFields = keyof ProfileValueOptionResponse;

type UserProfileTestScore = Readonly<{
  id: number;
  user_academics_id: number;
  score: number;
  created_at: string;
  updated_at: string;
}>;

type UserProfileApTest = UserProfileTestScore & {
  ap_test_id: number;
};

type UserProfileAcademicMajor = Readonly<{
  id: number;
  user_academics_id: number;
  academic_majors_id: number;
  major_gpa: number;
  created_at: string;
  updated_at: string;
}>;

type UserProfileEntranceExam = UserProfileTestScore & {
  entrance_exam_id: number;
};

export type UserProfileResponse = Readonly<{
  profile: {
    academicMetrics?: {
      high_school_gpa?: number;
      post_secondary_gpa?: number;
      sat_score?: number;
      act_score?: number;
      psat_score?: number;
    };

    academicEnrollmentTypes?: {
      id: number;
      code?: AcademicEnrollmentTypeCode;
      level: AcademicEnrollmentType;
    };
    academicHighSchoolExpectedGraduationYear?: number;
    academicPostSecondaryExpectedGraduationYear?: number;
    academicHighSchoolProgramStartTerm?: AcademicTerm;
    academicHighSchoolProgramStartYear?: string;
    academicPostSecondaryProgramStartTerm?: AcademicTerm;
    academicPostSecondaryProgramStartYear?: string;
    academicsHighSchoolCurrentEnrollment?:
      | string
      | {
          id: number;
          school: string;
        };

    academicsPostSecondaryCurrentEnrollment?:
      | string
      | {
          id: number;
          school: string;
        };

    academicsAlmaMaters?: ({ id: number; school: string } | string)[];

    academicApTests?: UserProfileApTest[];
    academicEntranceExams?: UserProfileEntranceExam[];

    academicExtracurricularInvolvement?: number;
    academicRigor?: number;
    academicDegrees?: number;
    academicMajors?: UserProfileAcademicMajor[];
    academicEducationalExperiences?: number[];

    careerOccupation?: string;
    careerExperiences?: number[];
    careerPaths?: number[];
    careerEmployer?: string[];

    coaching?: Coach;

    demographicsGenders?: number;
    demographicsEthnicities?: number;
    demographicsReligions?: number;
    demographicsAffinities?: number[];
    demographicsRelationshipExperiences?: number[];
    demographicsFamilyRoles?: number[];
    demographicsChildhoodExperiences: number[];
    demographicsMyersBriggs: number;
    demographicsHigh5s: number[];
    demographicsStrengthsFinders: number[];
    demographicsAstrologySigns: number;

    financialExperiences?: number[];
    financialCredentials?: number[];
    financialProfile?: number[];
    financialFamilyIncomes: number;

    healthMentalHealthInterests: ({ id: number; name: string } | string)[];
    healthInterests?: ({ id: number; name: string } | string)[];
    healthFoodNutrition?: ({ id: number; name: string } | string)[];
    healthExperiences?: number[];

    hobbiesCreativeInterests?: number[];
    hobbiesPassions?: number[];
    hobbiesFavorites?: number[];

    resourcesClubs?: ({ id: number; club: string } | string)[];
    resourcesLanguages?: ({ id: number; language: string } | string)[];

    documentsResumes?: StoryDocument[];
    documentsTranscripts?: StoryDocument[];
    documentsPersonalAssessment?: StoryDocument[];
    documentsOther?: StoryDocument[];

    documentsStoryVideos?: StoryDocument[];
    documentsPurposeVideo?: StoryDocument[];

    linkedInData?: {
      linkedIn_url?: LinkedInUrlData[];
      linkedIn_jobs?: StoryDocument[];
      linkedIn_education?: StoryDocument[];
      linkedIn_skills?: StoryDocument[];
    };

    calendlyData?: {
      calendly_url?: LinkedInUrlData[];
    };

    user_bio?: string;

    publicProfile: {
      id?: number;
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      profile_image?: string;
      profile_banner_image?: string;
      role: 'student' | 'professional';
      city?: string;
      state?: string;
      grade?: string;
    };
  };
}>;

/** The profile endpoints also report whether the student has finished the program on file. */
export type UserProfileFetchResponse = UserProfileResponse &
  Readonly<{
    graduated: boolean;
  }>;

type UpdateProfileRequest = Readonly<{
  field: ProfileFields;
  value:
    | number
    | string
    | (number | string)[]
    | (string | Record<string, string | number | null>)[]
    | Record<string, string | number | null>
    | null
    | Omit<StoryDocument, 'id' | 'media_url' | 'profile_option_answers_id'>
    | {
        gpa?: number;
        sat_score?: number;
        act_score?: number;
        psat_score?: number;
      }
    | {
        // Known properties from API autocomplete responses
        school?: string;
        club?: string;
        language?: string;
        name?: string;
        institution_id?: number | null;
        id?: number;
      };
}>;

/** Merges a profile mutation's response into the cached profile, and clears the
 * dashboard-featured cache when the enrollment type changed. */
const syncProfileAfterUpdate = async (
  queryFulfilled: Promise<{ data: UserProfileFetchResponse }>,
  dispatch: AppDispatch,
  enrollmentTypeChanged: boolean,
) => {
  const { data: updated } = await queryFulfilled;

  if (!updated?.profile) return;

  dispatch(
    profileApi.util.updateQueryData(
      'fetchUserProfileInfo',
      USER_PROFILE_FIELDS,
      (draft: WritableDraft<UserProfileFetchResponse>) => {
        draft.profile = {
          ...draft.profile,
          ...updated.profile,
        };
        draft.graduated = updated.graduated;
      },
    ),
  );

  if (enrollmentTypeChanged) {
    dispatch(dashboardFeaturedApi.util.resetApiState());
  }
};

export const profileApi = createApi({
  reducerPath: 'profileApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['UserProfile'],
  endpoints: (builder) => ({
    fetchProfileValueOptions: builder.query<ProfileValueOptionResponse, undefined>({
      query: () => 'profile-value-options',
    }),
    fetchUserProfileInfo: builder.query<UserProfileFetchResponse, ProfileFields[]>({
      query: (fields) => ({
        url: 'user-profile',
        method: 'GET',
        params: fields?.length ? { fields: fields.join(',') } : undefined,
      }),
      providesTags: ['UserProfile'],
    }),

    // Endpoint for fetching profile permissions for a user
    fetchProfilePermissions: builder.query<ProfilePermissions, { userId: string | undefined }>({
      query: ({ userId }) => {
        if (!userId) {
          throw new Error('userId is required');
        }

        return {
          url: `users/profile/permissions/${userId}`,
          method: 'GET',
        };
      },
    }),

    // New endpoint for fetching public profile
    fetchPublicProfileInfo: builder.query<UserProfileResponse, { userId: string | undefined }>({
      queryFn: async ({ userId }, queryApi, _extraOptions, baseQuery) => {
        if (!userId) {
          return { error: { status: 400, data: 'userId is required' } };
        }

        const state = queryApi.getState() as RootState;
        const isSharedSession: boolean = selectIsSharedSessionActive(state);

        const url: string = isSharedSession
          ? `shared/solution-session/public-profile/${userId}`
          : `user-profile/public/${userId}`;

        return baseQuery({ url, method: 'GET' }) as Promise<{ data: UserProfileResponse }>;
      },
    }),

    fetchAutocompleteOptions: builder.query<
      AutocompleteResult[],
      {
        endpoint?: string;
        searchTerm: string;
        displayProperty?: string;
        filter?: string;
        minSearchLength?: number;
      }
    >({
      query: ({ endpoint, searchTerm, filter, minSearchLength = 4 }) => {
        if (!searchTerm || searchTerm.length < minSearchLength || !endpoint) {
          throw new Error(`Search term must be at least ${minSearchLength} characters`);
        }

        const params: Record<string, string> = { q: searchTerm };

        if (filter) {
          params.type = filter;
        }

        return {
          url: endpoint,
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: AutocompleteResponse, _meta, { endpoint, displayProperty }) => {
        // Extract all values into a single array
        const values = response.results
          .map((item) => {
            // Check if this item has multiple properties (enhanced object)
            const itemKeys = Object.keys(item);
            const hasMultipleProperties = itemKeys.length > 1;

            if (hasMultipleProperties && displayProperty) {
              // Use configured display property - required for enhanced objects
              const displayValue = item[displayProperty];

              if (displayValue) {
                return {
                  label: displayValue,
                  value: displayValue,
                  ...item,
                };
              } else {
                return { label: '', value: '' };
              }
            }

            // Fallback: single property behavior for non-enhanced objects
            for (const key in item) {
              if (item[key]) {
                return { label: item[key], value: item[key] };
              }
            }

            return { label: '', value: '' };
          })
          .filter((item) => item.label !== '' || item.value !== ''); // Filter out empty options

        if (!endpoint) {
          throw new Error('Endpoint is required');
        }

        return [{ endpoint, values }];
      },
    }),

    previewGradeChange: builder.mutation<
      GradeChangePreview,
      // Null is a real answer here: clearing a year can finish a program.
      Partial<Record<ProfileFields, number | string | null>>
    >({
      query: (fields) => ({
        url: 'user-profile/grade-change-preview',
        method: 'POST',
        body: fields,
      }),
    }),

    updateUserProfileFields: builder.mutation<
      UserProfileFetchResponse,
      Partial<Record<ProfileFields, number | string>>
    >({
      query: (fields) => ({
        url: 'user-profile',
        method: 'PATCH',
        body: fields,
      }),
      invalidatesTags: ['UserProfile'],
      async onQueryStarted(fields, { queryFulfilled, dispatch }) {
        try {
          await syncProfileAfterUpdate(
            queryFulfilled,
            dispatch,
            'academicEnrollmentTypes' in fields,
          );
        } catch (error) {
          console.error('Failed to update profile fields:', Object.keys(fields), error);
        }
      },
    }),

    updateUserProfile: builder.mutation<UserProfileFetchResponse, UpdateProfileRequest>({
      query: ({ field, value }) => {
        return {
          url: 'user-profile',
          method: 'PATCH',
          body: { [field]: value },
        };
      },
      invalidatesTags: ['UserProfile'],
      async onQueryStarted({ field }, { queryFulfilled, dispatch }) {
        try {
          await syncProfileAfterUpdate(
            queryFulfilled,
            dispatch,
            field === 'academicEnrollmentTypes',
          );
        } catch (error) {
          console.error('Failed to update profile field:', field, error);
        }
      },
    }),
  }),
});

export const {
  useFetchProfileValueOptionsQuery,
  useFetchUserProfileInfoQuery,
  useFetchPublicProfileInfoQuery,
  useLazyFetchPublicProfileInfoQuery,
  useFetchProfilePermissionsQuery,
  useFetchAutocompleteOptionsQuery,
  useUpdateUserProfileMutation,
  usePreviewGradeChangeMutation,
  useUpdateUserProfileFieldsMutation,
  util: { resetApiState },
} = profileApi;
